// Die Kisten Bestellung, Termin, Montage (E3c, S3 und S4; G22) und seit S5
// Dachplanung – an Stelle der Platzhalter. Jede Seite sagt oben in einem Satz, wofür sie ist
// (G19); jede Zeile führt in die Akte an die Stelle, wo es erledigt wird.
//
//  - Bestellung: wo noch bestellt werden muss (der nächste Montagetermin
//    zuerst), mit dem, was fehlt, und dem Liefertermin – darunter, was zuletzt
//    bestellt wurde. GET /api/crm/bestellungen (Recht „bestellen“).
//  - Termin: alle Montagetermine der nächsten Wochen, je mit Dach- und
//    Bodenpartie und Uhrzeit; darunter die freigegebenen Anlagen ohne Tag.
//    GET /api/crm/montagen (Recht „einsatz“).
//  - Montage (MontagePlan, oben auf der Seite „Unterlagen“): je kommendem
//    Tag, welche Partie wo ist.
//  - Dachplanung (seit S5): wo der K2-Plan noch fehlt, darunter, wo die Liste
//    (der Excel-Report) fehlt – je die älteste Besichtigung zuerst.
//    GET /api/crm/dachplanungen (Recht „kunden“).

import type {
  BestellKisteAntwort,
  BestellKisteZeile,
  DachplanungKisteAntwort,
  DachplanungKisteZeile,
  MontagenAntwort,
  MontagePartie,
  MontageZeile,
} from "@happy-energy/kern";
import { Chip, Ikon, Tipp } from "@happy-energy/oberflaeche";
import { STAND_CHIP } from "../ablauf/kopf.tsx";
import { adresseZeigen, kalendertagZeigen, tagZeigen } from "../anzeige.ts";
import { Laedt, LadeFehler, useLaden } from "../laden.tsx";
import { Seite } from "../rahmen.tsx";
import { dachplanungSatz, montageTage, partieErledigt, partieSatz, teilSaetze } from "./kisten-zeilen.ts";
import "./kisten.css";

// --- Bestellung --------------------------------------------------------------------------------

function BestellZeile({ z, offen }: { z: BestellKisteZeile; offen: boolean }) {
  return (
    <li className="crm-karte kiste-zeile" aria-label={z.kundeName}>
      <div className="kiste-zeile-kopf">
        <div className="kiste-wer">
          <b>{z.kundeName}</b>
          <span className="leise klein">{adresseZeigen(z)}</span>
        </div>
        <span className={`kiste-termin${z.montagetermin.am ? "" : " fehlt"}`}>
          <Ikon name="kalender" groesse="k" /> {z.montagetermin.am ? `Montage ${kalendertagZeigen(z.montagetermin.am)}` : "noch kein Montagetermin"}
        </span>
      </div>
      {offen ? (
        <p className={z.stand === "wieder-offen" ? "akte-geaendert klein" : "klein"}>
          {/* „offen“ allein: Was noch offen ist, sagen die Zeilen je Teil darunter – kein „noch offen: …“ doppelt (S7). */}
          <Chip art={STAND_CHIP[z.stand]}>{z.stand === "geht-noch-nicht" ? "geht noch nicht" : z.stand === "wieder-offen" ? "wieder offen" : "offen"}</Chip>{" "}
          {z.stand === "offen" ? null : z.grund}
        </p>
      ) : (
        z.zuletztBestelltAm && <p className="klein leise">bestellt {tagZeigen(z.zuletztBestelltAm)}</p>
      )}
      <ul className="kiste-teile">
        {teilSaetze(z).map((t) => (
          <li key={t} className="klein">
            {t}
          </li>
        ))}
      </ul>
      <a className={`crm-knopf klein ${offen ? "dunkel" : "rand"}`} href={`#/anlage/${z.anlageId}/bestellung`}>
        {offen ? "Bestellen" : "Ansehen"} <Ikon name="rechts" groesse="k" />
      </a>
    </li>
  );
}

export function BestellungKiste() {
  const { daten, fehler, neu } = useLaden<BestellKisteAntwort>("/api/crm/bestellungen");
  return (
    <Seite titel="Bestellung" unter="Büro – beim Lieferanten">
      <p className="kiste-einleitung">Wo noch beim Lieferanten zu bestellen ist – der nächste Montagetermin zuerst. „Bestellen“ führt in die Akte.</p>
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!daten && fehler === undefined && <Laedt />}
      {daten && (
        <>
          <section aria-label="Noch zu bestellen">
            <div className="crm-abschnitt">
              <h2>Noch zu bestellen</h2>
              <span className="klein leise">{daten.offen.length === 1 ? "1 Anlage" : `${daten.offen.length} Anlagen`}</span>
            </div>
            {daten.offen.length === 0 ? (
              <Tipp>Gerade ist nichts zu bestellen. Eine Anlage steht hier, sobald sie freigegeben ist und noch nicht alles bestellt ist.</Tipp>
            ) : (
              <ul className="kiste-liste">
                {daten.offen.map((z) => (
                  <BestellZeile key={z.anlageId} z={z} offen />
                ))}
              </ul>
            )}
          </section>
          <section aria-label="Zuletzt bestellt">
            <div className="crm-abschnitt">
              <h2>Zuletzt bestellt</h2>
            </div>
            {daten.bestellt.length === 0 ? (
              <p className="leise klein">Noch nichts über das CRM bestellt.</p>
            ) : (
              <ul className="kiste-liste">
                {daten.bestellt.map((z) => (
                  <BestellZeile key={z.anlageId} z={z} offen={false} />
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </Seite>
  );
}

// --- Termin --------------------------------------------------------------------------------------

function Partie({ name, p, stelle, anlageId }: { name: string; p: MontagePartie; stelle: string; anlageId: string }) {
  return (
    <a className="kiste-partie" href={`#/anlage/${anlageId}/${stelle}`}>
      {/* Seit S7: der Stand neben dem Namen, die Partie darunter – nichts bricht mitten im Satz um. */}
      <span className="kiste-partie-kopf">
        <span className="leise klein">{name}</span>
        <Chip art={STAND_CHIP[p.stand]}>{partieErledigt(p.stand) ? "erledigt" : p.stand === "wieder-offen" ? "wieder offen" : p.stand === "geht-noch-nicht" ? "geht noch nicht" : "offen"}</Chip>
      </span>
      <span className="klein">{partieSatz(p)}</span>
    </a>
  );
}

function TerminZeile({ z }: { z: MontageZeile }) {
  return (
    <li className="crm-karte kiste-zeile" aria-label={z.kundeName}>
      <div className="kiste-zeile-kopf">
        <div className="kiste-wer">
          <b>{z.kundeName}</b>
          <span className="leise klein">{adresseZeigen(z)}</span>
        </div>
        <span className={`kiste-termin${z.montagetermin.am ? "" : " fehlt"}`}>
          <Ikon name="kalender" groesse="k" /> {z.montagetermin.am ? kalendertagZeigen(z.montagetermin.am) : "noch kein Tag"}
          {z.montagetermin.text && ` · ${z.montagetermin.text}`}
        </span>
      </div>
      <div className="kiste-partien">
        <Partie name="Dachpartie" p={z.dach} stelle="dachpartie" anlageId={z.anlageId} />
        <Partie name="Bodenpartie" p={z.boden} stelle="bodenpartie" anlageId={z.anlageId} />
      </div>
    </li>
  );
}

export function TerminKiste() {
  const { daten, fehler, neu } = useLaden<MontagenAntwort>("/api/crm/montagen");
  return (
    <Seite titel="Termin" unter="Die Montagetermine der nächsten Wochen">
      <p className="kiste-einleitung">Der nächste Montagetermin zuerst, je mit Dach- und Bodenpartie. Ein Klick auf eine Partie führt in die Akte.</p>
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!daten && fehler === undefined && <Laedt />}
      {daten && (
        <>
          <section aria-label="Kommende Montagen">
            <div className="crm-abschnitt">
              <h2>Kommende Montagen</h2>
              <span className="klein leise">{daten.kommend.length === 1 ? "1 Anlage" : `${daten.kommend.length} Anlagen`}</span>
            </div>
            {daten.kommend.length === 0 ? (
              <Tipp>Ab heute ist noch keine Montage eingetragen.</Tipp>
            ) : (
              <ul className="kiste-liste">
                {daten.kommend.map((z) => (
                  <TerminZeile key={z.anlageId} z={z} />
                ))}
              </ul>
            )}
          </section>
          <section aria-label="Freigegeben, noch ohne Termin">
            <div className="crm-abschnitt">
              <h2>Freigegeben, noch ohne Termin</h2>
              <span className="klein leise">{daten.ohneTermin.length === 1 ? "1 Anlage" : `${daten.ohneTermin.length} Anlagen`}</span>
            </div>
            {daten.ohneTermin.length === 0 ? (
              <p className="leise klein">Jede freigegebene Anlage hat einen Tag.</p>
            ) : (
              <ul className="kiste-liste">
                {daten.ohneTermin.map((z) => (
                  <li key={z.anlageId} className="crm-karte kiste-zeile" aria-label={z.kundeName}>
                    <div className="kiste-zeile-kopf">
                      <div className="kiste-wer">
                        <b>{z.kundeName}</b>
                        <span className="leise klein">{adresseZeigen(z)}</span>
                      </div>
                      <a className="crm-knopf dunkel klein" href={`#/anlage/${z.anlageId}/termin`}>
                        Termin eintragen <Ikon name="rechts" groesse="k" />
                      </a>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </Seite>
  );
}

// --- Dachplanung (seit S5) ------------------------------------------------------------------------

/** Eine Anlage in der Kiste Dachplanung – der Knopf sagt, wo es hochgeladen wird (G19). */
function DachplanungZeile({ z, planFehlt }: { z: DachplanungKisteZeile; planFehlt: boolean }) {
  return (
    <li className="crm-karte kiste-zeile" aria-label={z.kundeName}>
      <div className="kiste-zeile-kopf">
        <div className="kiste-wer">
          <b>{z.kundeName}</b>
          <span className="leise klein">{adresseZeigen(z)}</span>
        </div>
        <span className={`kiste-termin${z.montagetermin.am ? "" : " fehlt"}`}>
          <Ikon name="kalender" groesse="k" /> {z.montagetermin.am ? `Montage ${kalendertagZeigen(z.montagetermin.am)}` : "noch kein Montagetermin"}
        </span>
      </div>
      <p className="klein leise">{dachplanungSatz(z)}</p>
      {/* Den Plan lädt das Büro in der Akte hoch (Dachplanung), die Liste bei der Bestellung – dort steht sie zum Anhängen an die Mail.
          Seit 3.11.2026 „hochladen“ statt „anhängen“ – Gernot las das Anhängen an die Mail und das Hochladen als dasselbe. */}
      <a className="crm-knopf klein dunkel" href={planFehlt ? `#/anlage/${z.anlageId}` : `#/anlage/${z.anlageId}/bestellung`}>
        {planFehlt ? "Plan in der Akte hochladen" : "Liste bei der Bestellung hochladen"} <Ikon name="rechts" groesse="k" />
      </a>
    </li>
  );
}

export function DachplanungKiste() {
  const { daten, fehler, neu } = useLaden<DachplanungKisteAntwort>("/api/crm/dachplanungen");
  return (
    <Seite titel="Dachplanung" unter="K2 – draußen beim Verkauf">
      <p className="kiste-einleitung">Wo der K2-Plan oder die Liste (Excel) noch fehlt – die älteste Besichtigung zuerst. Ein Klick führt in die Akte.</p>
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!daten && fehler === undefined && <Laedt />}
      {daten && (
        <>
          <section aria-label="Noch ohne K2-Plan">
            <div className="crm-abschnitt">
              <h2>Noch ohne K2-Plan</h2>
              <span className="klein leise">{daten.ohnePlan.length === 1 ? "1 Anlage" : `${daten.ohnePlan.length} Anlagen`}</span>
            </div>
            {daten.ohnePlan.length === 0 ? (
              <Tipp>Jede gesendete Anlage hat ihren K2-Plan.</Tipp>
            ) : (
              <ul className="kiste-liste">
                {daten.ohnePlan.map((z) => (
                  <DachplanungZeile key={z.anlageId} z={z} planFehlt />
                ))}
              </ul>
            )}
          </section>
          <section aria-label="Plan da, Excel-Liste fehlt">
            <div className="crm-abschnitt">
              <h2>Plan da, Excel-Liste fehlt</h2>
              <span className="klein leise">{daten.ohneListe.length === 1 ? "1 Anlage" : `${daten.ohneListe.length} Anlagen`}</span>
            </div>
            {daten.ohneListe.length === 0 ? (
              <p className="leise klein">Wo der Plan da ist, ist auch die Liste da.</p>
            ) : (
              <ul className="kiste-liste">
                {daten.ohneListe.map((z) => (
                  <DachplanungZeile key={z.anlageId} z={z} planFehlt={false} />
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </Seite>
  );
}

// --- Montage -------------------------------------------------------------------------------------

/** Je kommendem Tag, welche Partie wo ist – oben auf der Seite der Unterlagen (Kiste Montage), für Leitung und Büro. */
export function MontagePlan() {
  const { daten, fehler, neu } = useLaden<MontagenAntwort>("/api/crm/montagen");
  const tage = daten ? montageTage(daten.kommend, daten.heute) : [];
  return (
    <section className="crm-karte montage-plan" aria-label="Welche Partie wann wo">
      <div className="karten-kopf">
        <Ikon name="werkzeug" />
        <h2>Welche Partie wann wo</h2>
      </div>
      <p className="leise klein akte-leer">Je Tag ab heute, welche Partie wo ist. Ein Klick auf die Adresse führt dorthin, wo sie eingeteilt wird.</p>
      {fehler !== undefined && <LadeFehler fehler={fehler} nochmal={neu} />}
      {!daten && fehler === undefined && <Laedt />}
      {daten && tage.length === 0 && <p className="leise klein">Ab heute ist noch keine Partie eingeteilt.</p>}
      {tage.map((t) => (
        <div key={t.tag} className="montage-tag" role="group" aria-label={kalendertagZeigen(t.tag)}>
          <h3>{kalendertagZeigen(t.tag)}</h3>
          <ul className="kiste-teile">
            {t.eintraege.map((e) => (
              <li key={`${e.zeile.anlageId}-${e.art}`} className={`montage-eintrag${e.partie ? "" : " fehlt"}`}>
                <span className="montage-wann">{e.uhrzeit ? e.uhrzeit : "–"}</span>
                <span className="montage-wer">
                  {e.partie ? (
                    <>
                      <b>{e.partie}</b> <span className="leise">{e.art === "dach" ? "Dach" : "Boden"}</span>
                    </>
                  ) : (
                    <b>{e.beide ? "noch keine Partie" : e.art === "dach" ? "noch keine Dachpartie" : "noch keine Bodenpartie"}</b>
                  )}
                </span>
                <a className="montage-wo" href={`#/anlage/${e.zeile.anlageId}/${e.art === "dach" ? "dachpartie" : "bodenpartie"}`}>
                  {e.zeile.kundeName} · {adresseZeigen(e.zeile)}
                </a>
                {e.partie && <Chip art={e.erledigt ? "ok" : "lime"}>{e.erledigt ? (e.art === "dach" ? "beauftragt" : "eingeteilt") : "offen"}</Chip>}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </section>
  );
}
